/**
 * Quick Start Templates Data
 * 
 * This file contains the predefined templates for different experience levels.
 * Following SOLID principles:
 * - Single Responsibility: Only contains template data
 * - Open/Closed: Easy to add new templates without modifying existing code
 * - Immutable: All data is readonly to prevent accidental modifications
 */

import { TemplateData } from '../../../types/Step0.types';

/**
 * Quick start templates for different experience levels
 * Each template provides a structured prompt for AI job post generation
 */
export const quickStartTemplates: readonly TemplateData[] = [
  {
    id: 'fresher',
    title: 'Fresher',
    subtitle: '(0-2 yrs)',
    icon: '',
    experience: 'Entry Level',
    description: `Position: [Entry-Level Position]
Experience: 0-2 years
Education: Bachelor's degree in relevant field
Key Skills: Basic programming, communication, willingness to learn
Training: On-the-job training provided
Team: Work under senior mentorship
Additional: Fresh graduates encouraged to apply`,
  },
  {
    id: 'mid-level',
    title: 'Mid-Level',
    subtitle: '(3-5 yrs)',
    icon: '',
    experience: 'Intermediate',
    description: `Position: [Mid-Level Position]
Experience: 3-5 years in relevant field
Key Skills: Proven track record, independent problem-solving
Responsibilities: Lead small projects, mentor juniors
Technical: Advanced knowledge in core technologies
Team: Collaborate with cross-functional teams`,
  },
  {
    id: 'senior',
    title: 'Senior',
    subtitle: '(6-8 yrs)',
    icon: '',
    experience: 'Advanced',
    description: `Position: [Senior Position]
Experience: 6-8 years of progressive experience
Key Skills: Expert-level technical skills, architectural decisions
Responsibilities: Lead complex projects, guide team direction
Leadership: Mentor team members, code reviews
Stakeholder: Direct interaction with clients`,
  },
  {
    id: 'expert',
    title: 'Expert',
    subtitle: '(9+ yrs)',
    icon: '',
    experience: 'Expert/Principal',
    description: `Position: [Expert/Principal Position]
Experience: 9+ years with deep specialization
Key Skills: Industry thought leader, innovative solutions
Responsibilities: Define technical vision and strategy
Leadership: Lead multiple teams, influence org-wide decisions
External: Conference speaking, open-source contributions`,
  },
] as const;

/**
 * Helper function to get template by ID
 * Provides type-safe template retrieval
 */
export const getTemplateById = (id: string): TemplateData | undefined => {
  return quickStartTemplates.find(template => template.id === id);
};

/**
 * Helper function to get all template IDs
 * Useful for validation and iteration
 */
export const getAllTemplateIds = (): string[] => {
  return quickStartTemplates.map(template => template.id);
};

/**
 * Helper function to get templates by experience level
 * Allows filtering templates by experience range
 */
export const getTemplatesByExperience = (minYears: number, maxYears?: number): TemplateData[] => {
  return quickStartTemplates.filter(template => {
    const match = /\d+/.exec(template.subtitle);
    const years = Number.parseInt(match?.[0] || '0', 10);
    if (maxYears === undefined) {
      return years >= minYears;
    }
    return years >= minYears && years <= maxYears;
  });
};
